import React, { useRef, useState, useCallback, useEffect } from 'react';
import styles from './ScrimTimeline.module.css';

interface VideoInfo {
  id: string;
  startTime: Date;
  duration: number;
  title: string;
}

interface ScrimTimelineProps {
  videoInfos: VideoInfo[];
  globalStartTime: Date | null;
  currentTime: number;
  onSeek: (time: number) => void;
  isPlaying: boolean;
  isSeekingToTarget: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  playButton?: React.ReactNode;
  globalEndTime?: Date | null;
  // New optional props for a focused time range (e.g., selected round)
  rangeStartSeconds?: number | null; // absolute seconds from globalStartTime
  rangeEndSeconds?: number | null;   // absolute seconds from globalStartTime
  timerMode?: 'normal' | 'countdown100';
}

const ScrimTimeline: React.FC<ScrimTimelineProps> = ({
  videoInfos,
  globalStartTime,
  currentTime,
  onSeek,
  isPlaying,
  isSeekingToTarget,
  onDragStart,
  onDragEnd,
  playButton,
  globalEndTime,
  rangeStartSeconds = null,
  rangeEndSeconds = null,
  timerMode = 'normal',
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const seekDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastSeekTimeRef = useRef<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [internalTime, setInternalTime] = useState(0);
  const [timelineBounds, setTimelineBounds] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
  const [timelineWidth, setTimelineWidth] = useState(0);

  // When focusing on a range, treat "0" as the start of that range for visual position/time
  const effectiveExternalTime = (rangeStartSeconds != null)
    ? currentTime - rangeStartSeconds
    : currentTime;

  // Use internal time during dragging or seeking, external time otherwise
  const displayTime = (isDragging || isSeekingToTarget) ? internalTime : effectiveExternalTime;

  // Sync internal time with external time when not dragging (allow updates during seeking)
  useEffect(() => {
    if (!isDragging) {
      setInternalTime(effectiveExternalTime);
    }
  }, [effectiveExternalTime, isDragging]);

  // Calculate timeline bounds from all videos or explicit overlap end, or a provided range
  useEffect(() => {
    if (!globalStartTime || videoInfos.length === 0) {
      setTimelineBounds({ min: 0, max: 0 });
      return;
    }

    // If a focused range is provided, use that exclusively
    if (rangeStartSeconds != null && rangeEndSeconds != null) {
      const maxTime = Math.max(0, rangeEndSeconds - rangeStartSeconds);
      setTimelineBounds({ min: 0, max: maxTime });
      return;
    }

    let minTime = 0; // Global start is always 0
    let maxTime = 0;

    if (globalEndTime) {
      // Use explicit overlap end if provided
      maxTime = Math.max(0, (globalEndTime.getTime() - globalStartTime.getTime()) / 1000);
    } else {
      // Fallback to full union if no explicit end provided
      videoInfos.forEach(video => {
        const offsetSeconds = (video.startTime.getTime() - globalStartTime.getTime()) / 1000;
        const endTime = offsetSeconds + video.duration;
        maxTime = Math.max(maxTime, endTime);
      });
    }

    setTimelineBounds({ min: minTime, max: maxTime });
  }, [videoInfos, globalStartTime, globalEndTime, rangeStartSeconds, rangeEndSeconds]);

  const getTimelineWidth = useCallback(() => {
    return timelineWidth || timelineRef.current?.clientWidth || 0;
  }, [timelineWidth]);

  const timeToPosition = useCallback((time: number): number => {
    const { min, max } = timelineBounds;
    if (max <= min) return 0;

    const percentage = (time - min) / (max - min);
    return Math.max(0, Math.min(1, percentage)) * getTimelineWidth();
  }, [timelineBounds, getTimelineWidth]);

  const positionToTime = useCallback((position: number): number => {
    const { min, max } = timelineBounds;
    if (max <= min) return 0;

    const percentage = position / getTimelineWidth();
    return min + (percentage * (max - min));
  }, [timelineBounds, getTimelineWidth]);

  const debouncedSeek = useCallback((time: number, immediate: boolean = false) => {
    // Always update internal time immediately for responsive visual feedback
    setInternalTime(time);
    lastSeekTimeRef.current = time;

    const absoluteSeekTime = (rangeStartSeconds != null) ? (rangeStartSeconds + time) : time;

    if (immediate) {
      // Clear any pending debounced seek and execute immediately
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
        seekDebounceRef.current = null;
      }
      onSeek(absoluteSeekTime);
    } else {
      // Debounce the seek request
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }

      seekDebounceRef.current = setTimeout(() => {
        onSeek(absoluteSeekTime);
        seekDebounceRef.current = null;
      }, 100); // 100ms debounce
    }
  }, [onSeek, rangeStartSeconds]);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!timelineRef.current) return;

    setIsDragging(true);
    onDragStart(); // Notify parent that dragging started
    const rect = timelineRef.current.getBoundingClientRect();
    const position = event.clientX - rect.left;
    const time = positionToTime(position);
    debouncedSeek(time, true); // Immediate seek on mouse down
  }, [positionToTime, debouncedSeek, onDragStart]);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const position = Math.max(0, Math.min(getTimelineWidth(), event.clientX - rect.left));
    const time = positionToTime(position);
    debouncedSeek(time, false); // Debounced seek during drag
  }, [isDragging, positionToTime, debouncedSeek, getTimelineWidth]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);

    // Ensure final seek with the last internal time when drag ends
    debouncedSeek(lastSeekTimeRef.current, true);

    onDragEnd(); // Notify parent that dragging ended
    // Internal time will sync with external time via useEffect
  }, [debouncedSeek, onDragEnd]);

  // Global mouse events for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle window resize to recalculate timeline width
  useEffect(() => {
    const updateTimelineWidth = () => {
      if (timelineRef.current) {
        setTimelineWidth(timelineRef.current.clientWidth);
      }
    };

    // Initial calculation
    updateTimelineWidth();

    // Add resize listener
    window.addEventListener('resize', updateTimelineWidth);

    return () => {
      window.removeEventListener('resize', updateTimelineWidth);
    };
  }, []);

  // Update timeline width when timeline bounds change (component layout may have changed)
  useEffect(() => {
    if (timelineRef.current) {
      setTimelineWidth(timelineRef.current.clientWidth);
    }
  }, [timelineBounds]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
    };
  }, []);

  const formatTime = (seconds: number): string => {
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = Math.floor(total % 60);

    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getVideoSegments = () => {
    if (!globalStartTime) return [];

    return videoInfos.map(video => {
      const offsetSeconds = (video.startTime.getTime() - globalStartTime.getTime()) / 1000;
      const startPos = timeToPosition(offsetSeconds);
      const endPos = timeToPosition(offsetSeconds + video.duration);
      const width = endPos - startPos;

      return {
        id: video.id,
        title: video.title,
        left: startPos,
        width: Math.max(2, width), // Minimum width for visibility
        startTime: offsetSeconds,
        endTime: offsetSeconds + video.duration
      };
    });
  };

  if (timelineBounds.max <= timelineBounds.min) {
    return null; // Don't render if no valid timeline
  }

  const currentPosition = timeToPosition(displayTime);
  const videoSegments = getVideoSegments();

  // Calculate progress percentage for timeline fill (clamped to [0, 100])
  const progressPercentage = timelineBounds.max > timelineBounds.min 
    ? Math.min(100, Math.max(0, ((displayTime - timelineBounds.min) / (timelineBounds.max - timelineBounds.min)) * 100))
    : 0;

  // Determine label time: countdown from 100s in round mode if requested; otherwise show elapsed
  const labelSeconds = timerMode === 'countdown100'
    ? Math.max(0, 100 - displayTime)
    : displayTime;

  const isRangeMode = rangeStartSeconds != null && rangeEndSeconds != null;

  return (
    <div className={styles.timeControlContainer}>
      <div className={styles.timelineRow}>
        <div className={styles.leftColumnPlayPause}>
          {playButton && <div className={styles.playButtonContainer}>{playButton}</div>}
        </div>
        <div className={styles.rightColumn}>
          <div 
            className={`${styles.timeline} ${isDragging ? styles.dragging : ''}`}
            ref={timelineRef}
            onMouseDown={handleMouseDown}
            style={{
              '--slider-fill': `${progressPercentage}%`
            } as React.CSSProperties}
          >            
            {/* Current time indicator */}
            <div 
              className={`${styles.timeIndicator} ${isDragging ? styles.dragging : ''}`}
              style={{ left: `${currentPosition}px` }}
            />
            
            {/* Playhead handle */}
            <div 
              className={`${styles.playhead} ${isDragging ? styles.dragging : ''}`}
              style={{ left: `${currentPosition - 8}px` }}
            />
          </div>
          
          {!isRangeMode && (
            <div className={styles.currentTimeLabel}>
              {formatTime(labelSeconds)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScrimTimeline;